import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigationType,
  useParams,
  useSearchParams,
} from "react-router";

import { queryKeys } from "@/api/hooks";
import { usePreviewCandidatesQuery } from "@/api/preview";
import { useCapability } from "@/auth/SessionContext";
import {
  defaultLabCandidateId,
  sortCandidatesNewestFirst,
} from "@/lib/labModel";
import { cn } from "@/lib/cn";
import { latestTurnNumber } from "@/lib/previewManual";
import { buildLabRunModel, type LabRunModel } from "@/lib/labRunModel";
import {
  LAB_SCROLL_PANE,
  effectivePane,
  effectiveTab,
  readRunsCollapsed,
  writeRunsCollapsed,
} from "@/lib/labLayout";
import {
  readLabSelection,
  writeLabSelection,
  type LabPaneId,
  type LabSelection,
  type LabTabId,
} from "@/lib/labUrl";
import type { LabPendingLine, LabRunData } from "@/lib/labModel";
import { CompanionWarnings } from "@/ui/files/CompanionWarnings";
import { LabAcceptPreview } from "@/ui/lab/LabAcceptPreview";
import { LabComposer, LabComposerIdle } from "@/ui/lab/LabComposer";
import {
  LabContinueFromTurn,
  LabUndoLastMessage,
} from "@/ui/lab/LabContinueFromTurn";
import { LabConversation } from "@/ui/lab/LabConversation";
import { LabInspectorPane } from "@/ui/lab/LabInspectorPane";
import { LabNewRunForm } from "@/ui/lab/LabNewRunForm";
import { LabRunsPane } from "@/ui/lab/LabRunsPane";
import { LabRunsRail } from "@/ui/lab/LabRunsRail";
import { LabTopBar } from "@/ui/lab/LabTopBar";
import { useLabRunData } from "@/ui/lab/useLabRunData";
import { ToastProvider } from "@/ui/Toast";
import { DeniedState } from "@/ui/states";

/** The run model for one run's data (null-safe; rebuilt only when its inputs change). */
function useRunModel(data: LabRunData | null): LabRunModel | null {
  const previewRunRef = data?.previewRunRef ?? null;
  const view = data?.view ?? null;
  const sim = data?.sim ?? null;
  const records = data?.records ?? null;
  return useMemo(
    () =>
      previewRunRef === null
        ? null
        : buildLabRunModel({
            previewRunRef,
            view,
            sim,
            records: records ?? [],
          }),
    [previewRunRef, view, sim, records],
  );
}

/**
 * Lab: a full-screen, three-pane workspace over one project's preview runs.
 * Registered OUTSIDE the app shell. The address names the run, turn and
 * comparison run (opaque refs and a number, nothing about the patient); the
 * testing version used for "New run" is route state and never in the address.
 */
export function ProjectLabRoute() {
  const { projectId = "" } = useParams();
  const inspect = useCapability("preview.inspect");
  const create = useCapability("preview.create");
  const [searchParams, setSearchParams] = useSearchParams();
  const selection = useMemo(
    () => readLabSelection(searchParams),
    [searchParams],
  );
  // A new run is a navigation; moving between turns only refines the page.
  const select = (patch: Partial<LabSelection>) =>
    setSearchParams((current) => writeLabSelection(current, patch), {
      replace: !("run" in patch),
    });

  const candidates = usePreviewCandidatesQuery(projectId);
  const items = useMemo(
    () => sortCandidatesNewestFirst(candidates.data?.items ?? []),
    [candidates.data],
  );
  // The clinician's choice holds while it still exists; otherwise the default.
  // "Test in Lab" on the Pack files page hands its fresh candidate over as
  // route state (never in the address).
  const location = useLocation();
  const fromState = (location.state as { candidateId?: unknown } | null)
    ?.candidateId;
  const [chosen, setChosen] = useState<string | null>(
    typeof fromState === "string" ? fromState : null,
  );
  const candidateId =
    chosen !== null && items.some((item) => item.candidateId === chosen)
      ? chosen
      : defaultLabCandidateId(items);
  // Compare mode (top bar): the centre splits into two runs once a second is picked.
  const [compareArmed, setCompareArmed] = useState(selection.compare !== null);
  // The runs pane folds to a rail; the choice is remembered per browser.
  const [runsCollapsed, setRunsCollapsedState] = useState(readRunsCollapsed);
  const setRunsCollapsed = useCallback((collapsed: boolean) => {
    setRunsCollapsedState(collapsed);
    writeRunsCollapsed(collapsed);
  }, []);
  // Narrow screens: the inspector is a bottom panel, closed until a turn is picked.
  const [sheetOpen, setSheetOpen] = useState(false);
  // Bumped when a selection made in the inspector should re-scroll the conversation.
  const [scrollSignal, setScrollSignal] = useState(0);

  const queryClient = useQueryClient();
  const started = (previewRunRef: string) => {
    select({ run: previewRunRef, turn: null, compare: null });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.previewHistory(projectId),
    });
  };

  const primary = useLabRunData(projectId, selection.run);
  const compare = useLabRunData(projectId, selection.compare);
  const primaryModel = useRunModel(primary);
  const compareModel = useRunModel(compare);

  const pane = effectivePane(selection);
  const tab = effectiveTab(pane, selection.tab, compareArmed);
  // Manual sessions: the inspector follows the latest turn until the clinician
  // picks another one (which pins it); the composer's toggle turns it back on.
  // A link that names a turn opens pinned to it, not the latest one.
  const [followLive, setFollowLive] = useState(selection.turn === null);
  const [pendingLine, setPendingLine] = useState<LabPendingLine | null>(null);
  const [followRef, setFollowRef] = useState(selection.run);
  if (followRef !== selection.run) {
    setFollowRef(selection.run);
    setFollowLive(selection.turn === null);
  }
  const latestTurn = latestTurnNumber(primary?.lab.turns ?? []);
  const following = followLive && primary?.manual != null;
  useEffect(() => {
    if (following && latestTurn !== null && selection.turn !== latestTurn) {
      select({ turn: latestTurn });
    }
    // `select` only wraps setSearchParams; the inputs below are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [following, latestTurn, selection.turn]);
  // Picking a turn in the conversation shows it under "This turn".
  const onSelectTurnInConversation = (turn: number) => {
    setFollowLive(false);
    select({ turn, pane: "turn" });
    setSheetOpen(true);
  };
  // Picking a turn in the inspector (timeline, graph, prompts, raw events,
  // metrics...) keeps its scope and scrolls the conversation to the turn.
  const onSelectTurnInInspector = (turn: number) => {
    setFollowLive(false);
    select({ turn, pane });
    setScrollSignal((n) => n + 1);
  };
  const onPaneChange = (next: LabPaneId) => select({ pane: next, tab: null });
  const onTabChange = (next: LabTabId) => select({ pane, tab: next });
  const toggleCompare = () => {
    if (compareArmed) {
      setCompareArmed(false);
      // One write: two `select` calls share the render's params, so the second would restore `compare`.
      const leavesCompareTab = pane === "run" && selection.tab === "compare";
      select(leavesCompareTab ? { compare: null, tab: null } : { compare: null });
    } else {
      setCompareArmed(true);
      setRunsCollapsed(false); // the second run is picked in the list
    }
  };

  // Focus lands on the heading after an in-app navigation (as the run page does).
  const navigationType = useNavigationType();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (navigationType === "PUSH") {
      headingRef.current?.focus();
    }
  }, [navigationType]);

  const topBar = (
    <LabTopBar
      projectId={projectId}
      candidateId={candidateId}
      onCandidateChange={setChosen}
      headingRef={headingRef}
      compareOn={compareArmed}
      compareHasSecond={selection.compare !== null}
      onCompareToggle={inspect.allowed ? toggleCompare : undefined}
    />
  );

  return (
    <ToastProvider>
      <div className="flex h-screen flex-col bg-background text-foreground max-[899px]:h-auto max-[899px]:min-h-screen">
        <a
          href="#lab-main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:text-sm focus:shadow-md"
        >
          Skip to main content
        </a>
        {topBar}
        {inspect.allowed ? (
          <LabNewRunForm
            projectId={projectId}
            candidateId={candidateId}
            onStarted={started}
            more={
              <LabAcceptPreview
                projectId={projectId}
                selectedCandidateId={candidateId}
              />
            }
          />
        ) : null}
        {inspect.allowed ? (
          <CompanionWarnings
            projectId={projectId}
            fixable={false}
            className="px-4 pt-3"
          />
        ) : null}
        {inspect.allowed ? (
          <main
            id="lab-main"
            className="flex min-h-0 flex-1 flex-row max-[899px]:flex-col max-[899px]:pb-10"
          >
            <div className="max-[899px]:order-2">
              {runsCollapsed ? (
                <LabRunsRail onExpand={() => setRunsCollapsed(false)} />
              ) : (
                <div
                  className={cn(
                    "h-full border-border min-[900px]:w-[300px] min-[900px]:border-e",
                    LAB_SCROLL_PANE,
                  )}
                >
                  <LabRunsPane
                    projectId={projectId}
                    selection={selection}
                    onSelect={select}
                    compareArmed={compareArmed}
                    onCollapse={() => setRunsCollapsed(true)}
                  />
                </div>
              )}
            </div>
            <div className="relative grid min-h-0 min-w-0 flex-1 grid-cols-1 min-[900px]:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)] min-[900px]:grid-rows-[minmax(0,1fr)] max-[899px]:order-1">
              <div className="flex min-h-0 min-w-0 flex-col border-border min-[900px]:border-e">
                <div className="relative min-h-0 flex-1 p-4 min-[900px]:overflow-y-auto">
                  <LabConversation
                    projectId={projectId}
                    primary={primary}
                    compare={compare}
                    selectedTurn={selection.turn}
                    onSelectTurn={onSelectTurnInConversation}
                    scrollSignal={scrollSignal}
                    pendingLine={pendingLine}
                    followLive={following}
                    candidateId={candidateId}
                    onContinued={(run) =>
                      select({ run, turn: null, compare: null })
                    }
                  />
                </div>
                {selection.run === null && create.allowed ? (
                  <div className="shrink-0 border-t border-border bg-background p-3 max-[899px]:sticky max-[899px]:bottom-10">
                    <LabComposerIdle />
                  </div>
                ) : null}
                {primary?.manual != null && create.allowed ? (
                  <div className="shrink-0 border-t border-border bg-background p-3 max-[899px]:sticky max-[899px]:bottom-10">
                    <LabComposer
                      key={primary.previewRunRef}
                      projectId={projectId}
                      previewRunRef={primary.previewRunRef}
                      manual={primary.manual}
                      language={primary.manual.language ?? undefined}
                      followLive={followLive}
                      onFollowLiveChange={setFollowLive}
                      onPendingLine={(line) => {
                        setPendingLine(line);
                        // Sending brings the line, then Liv's reply, into view.
                        if (line?.status === "sending") {
                          setFollowLive(true);
                        }
                      }}
                      continueFrom={(turnNumber) => (
                        <LabContinueFromTurn
                          key={`${primary.previewRunRef}-${turnNumber}`}
                          projectId={projectId}
                          previewRunRef={primary.previewRunRef}
                          turnNumber={turnNumber}
                          candidateId={candidateId}
                          fixedLanguage={primary.manual?.language ?? null}
                          quick
                          onContinued={(run) =>
                            select({ run, turn: null, compare: null })
                          }
                        />
                      )}
                      actions={
                        <LabUndoLastMessage
                          key={primary.previewRunRef}
                          projectId={projectId}
                          previewRunRef={primary.previewRunRef}
                          manual={primary.manual}
                          candidateId={candidateId}
                          onContinued={(run) =>
                            select({ run, turn: null, compare: null })
                          }
                        />
                      }
                    />
                  </div>
                ) : null}
              </div>
              <LabInspectorPane
                projectId={projectId}
                primary={primary}
                compare={compare}
                primaryModel={primaryModel}
                compareModel={compareModel}
                selectedTurn={selection.turn}
                pane={pane}
                tab={tab}
                compareOn={compareArmed}
                onPaneChange={onPaneChange}
                onTabChange={onTabChange}
                onSelectTurn={onSelectTurnInInspector}
                onOpenRuns={(run, other) => {
                  setCompareArmed(true);
                  select({ run, turn: null, compare: other });
                }}
                sheetOpen={sheetOpen}
                onSheetOpenChange={setSheetOpen}
              />
            </div>
          </main>
        ) : (
          <main id="lab-main" className="flex-1 p-6">
            <DeniedState
              title="Reliv Labs is not available to your account"
              description={`${inspect.reason ?? ""} Inspecting a preview run needs the "preview.inspect" permission, which Project Editors, Clinical Managers and Administrators hold. Ask a workspace administrator if you need it.`}
            />
          </main>
        )}
      </div>
    </ToastProvider>
  );
}
